import React, { useState } from 'react'

import {
  Button, TextInput, ArrowLeft, ArrowRight, Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { number, shape, string } from 'prop-types'

const DEFAULT_LABELS = {
  previous: 'Go to previous page',
  next: 'Go to next page',
  jump: 'Jump to page',
}

const PAGE_FORMAT = /^[1-9]\d*$/

function LinkedPagination({
  currentPage,
  totalPages,
  currentPath,
  labels = DEFAULT_LABELS,
  className = '',
}) {
  const numericCurrentPage = Number(currentPage) || 1
  const numericTotalPages = Number(totalPages) || 1
  const isGoBackDisabled = numericCurrentPage <= 1
  const isGoNextDisabled = numericCurrentPage >= numericTotalPages
  const [inputValue, setInputValue] = useState(numericCurrentPage)

  function getButtonHref(page) {
    if (page <= 1 || page > numericTotalPages) {
      return currentPath
    }

    return `${currentPath}?page=${page}`
  }

  function handleButtonClick(event, isDisabled) {
    if (isDisabled) {
      event.preventDefault()
    }
  }

  function handleChange(event) {
    const { value } = event.target
    const valueIsNumber = !Number.isNaN(Number(value))

    if (valueIsNumber) {
      return setInputValue(value)
    }

    return null
  }

  function handleJumpToPage(event) {
    const hasValue = !!inputValue
    const numericValue = Number(inputValue)
    const valueIsNumber = !Number.isNaN(numericValue)
    const mismatchesRange = numericValue < 1 || numericValue > numericTotalPages
    const isWholePage = PAGE_FORMAT.test(String(inputValue))
    const isInputSubmitted = event.key === 'Enter'

    if (isInputSubmitted && hasValue && valueIsNumber) {
      if (mismatchesRange || !isWholePage) {
        return setInputValue(numericCurrentPage)
      }

      return window.location.href = getButtonHref(numericValue)
    }

    return null
  }

  if (numericTotalPages === 1) {
    return null
  }

  return (
    <div className={`row justify-content-center new-linked-pagination ${className}`.trim()}>
      <Button
        type="secondary"
        href={getButtonHref(1)}
        disabled={isGoBackDisabled}
        onClick={(e) => { handleButtonClick(e, isGoBackDisabled) }}
        customClass="new-linked-pagination__button"
      >
        1
      </Button>
      <Button
        title={labels.previous}
        icon={<Icon svgComponent={ArrowLeft} />}
        type="primary"
        variant="only-icon"
        disabled={isGoBackDisabled}
        href={getButtonHref(numericCurrentPage - 1)}
        onClick={(e) => { handleButtonClick(e, isGoBackDisabled) }}
        customClass="new-linked-pagination__button new-linked-pagination__button--wide"
      />
      <div className="new-linked-pagination__input">
        <TextInput
          id="pageInput"
          className="new-linked-pagination__input-field"
          inputType="text"
          aria-label={labels.jump}
          isPilot
          value={inputValue}
          onChange={handleChange}
          size="medium"
          type="primary"
          onKeyPress={handleJumpToPage}
        />
      </div>
      <Button
        title={labels.next}
        icon={<Icon svgComponent={ArrowRight} />}
        type="primary"
        variant="only-icon"
        disabled={isGoNextDisabled}
        href={getButtonHref(numericCurrentPage + 1)}
        onClick={(e) => { handleButtonClick(e, isGoNextDisabled) }}
        customClass="new-linked-pagination__button new-linked-pagination__button--wide"
      />
      <Button
        type="secondary"
        disabled={isGoNextDisabled}
        href={getButtonHref(numericTotalPages)}
        onClick={(e) => { handleButtonClick(e, isGoNextDisabled) }}
        customClass="new-linked-pagination__button"
      >
        {numericTotalPages}
      </Button>
    </div>
  )
}

LinkedPagination.propTypes = {
  currentPage: number.isRequired,
  totalPages: number.isRequired,
  currentPath: string.isRequired,
  labels: shape({
    previous: string,
    next: string,
    jump: string,
  }),
  className: string,
}

export default LinkedPagination
